import { CardBrand } from '@/features/fraud/card-brand'
import type { Order, Transaction } from '@/lib/api/types'
import { isCardSale } from '@/lib/payment-card'

const money = (amount: number) => `$${amount.toFixed(2)}`

/**
 * The card behind a POS card sale: what the guest's card was charged. The
 * order total above stays the vendor's sale (the items, before tax and
 * fees); the charge adds what the guest paid on top.
 */
export function OrderCardPayment({ order }: { order: Order }) {
  const sale = (order.transactionlog_set ?? []).find(
    (row): row is Transaction => typeof row === 'object' && row !== null && isCardSale(row),
  )
  if (!sale) return null
  const breakdown = sale.fee_breakdown
  const extras = breakdown?.line_items?.filter((item) => item.paid_by === 'user' && item.amount > 0) ?? []
  const charged = breakdown?.total_amount ?? sale.total_amount
  const card = sale.payment_card
  const refund = order.status === 'refunded' || sale.refunded === 1
    ? 'Refunded'
    : order.status === 'partial' || sale.refunded === 2 ? 'Partially refunded' : null

  const row = 'grid grid-cols-[140px_1fr] gap-2'
  const label = 'font-semibold text-neutral-700 dark:text-neutral-300 text-sm'
  const value = 'text-neutral-900 dark:text-neutral-100 text-sm'
  return (
    <div className="bg-white dark:bg-neutral-900 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
      <h3 className="flex items-center text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500">
          <rect x="2" y="5" width="20" height="14" rx="2"></rect>
          <line x1="2" y1="10" x2="22" y2="10"></line>
        </svg>
        Payment
      </h3>
      <div className="space-y-3">
        <div className={row}>
          <span className={label}>Paid by:</span>
          <span className={value}>
            Card, in person
            {card && (
              <>
                {' · '}
                <CardBrand brand={card.brand} last4={card.last4} wallet={card.wallet} />
              </>
            )}
          </span>
        </div>
        {charged != null && (
          <div className={row}>
            <span className={label}>Card charged:</span>
            <span className={value}>
              {money(charged)}
              {breakdown?.base_amount != null && extras.length > 0 && (
                <span className="text-neutral-500 dark:text-neutral-400">
                  {' '}({[`items ${money(breakdown.base_amount)}`,
                    ...extras.map((item) => `${item.name.toLowerCase()} ${money(item.amount)}`)].join(' + ')})
                </span>
              )}
            </span>
          </div>
        )}
        {sale.stripe_charge_id && (
          <div className={row}>
            <span className={label}>Finix transfer:</span>
            <span className={`${value} font-mono text-xs break-all`}>{sale.stripe_charge_id}</span>
          </div>
        )}
        {refund && (
          <div className={row}>
            <span className={label}>Refund:</span>
            <span className={value}>{refund}</span>
          </div>
        )}
      </div>
    </div>
  )
}
